import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { endpoint } from '@/app/_module/services/endpoints';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';

import type { LeadAssignmentRequest, LeadAssignmentResponse } from '../types/leadassign-types';

export const leadAssignmentService = {
  assignLead: async (data: LeadAssignmentRequest): Promise<LeadAssignmentResponse> => {
    // Backend expects: { leadIds: string[], agentId: string }
    // assignedAgencyId is extracted from JWT token, not sent in body
    const requestBody = {
      leadIds: data.leadIds,
      agentId: data.agentId,
    };
    
    const response = await handleApiCall(async () => await api.post({
      url: getApiUrl(endpoint.leadAssignment.assignLead),
      body: requestBody, // Use 'body' not 'data' - matches api.post signature
      auth: true,
    }));
    return ((response as any).data?.data || (response as any).data) as LeadAssignmentResponse;
  },

  unassignLead: async (leadIds: string[], includeClosed = false): Promise<{ success: boolean; message: string; unassignedCount?: number; skippedClosedIds?: string[] }> => {
    const response = await handleApiCall(async () => await api.post({
      url: getApiUrl(endpoint.leadAssignment.unassignLead),
      body: { leadIds, includeClosed },
      auth: true,
      // A 2,000-lead batch can outlast the global 60s timeout on a busy DB (same as assign).
      options: { timeout: 90_000 },
    }));
    // handleApiCall already unwrapped the envelope; reading .data again returned undefined.
    const r = response as any;
    return (r?.data?.data ?? r?.data ?? r) as { success: boolean; message: string; unassignedCount?: number; skippedClosedIds?: string[] };
  },

  /** Which of these leads are sold/closed (and would be kept by a plain unassign). */
  previewUnassign: async (leadIds: string[]): Promise<{ total: number; closedCount: number; closedIds: string[] }> => {
    const response = await handleApiCall(async () => await api.post({
      url: getApiUrl(endpoint.leadAssignment.unassignLead) + "/preview",
      body: { leadIds },
      auth: true,
    }));
    // Same: this returned undefined on every success since 2026-09-17, so the sold-lead
    // warning never showed and Unassign-by-list crashed on "closedCount" (Moe Ray, 2026-10-07).
    const r = response as any;
    return (r?.data?.data ?? r?.data ?? r) as { total: number; closedCount: number; closedIds: string[] };
  },

  getAgentWorkload: async () => {
    const response = await handleApiCall(async () => await api.get({
      url: getApiUrl(endpoint.leadAssignment.getAgentWorkload),
    }));
    return (response as any).data?.data || (response as any).data;
  },

  getStatusTracking: async () => {
    const response = await handleApiCall(async () => await api.get({
      url: getApiUrl(endpoint.leadAssignment.getStatusTracking),
      auth: true,
    }));
    // handleApiCall returns { statusCode, message, data, timestamp }
    // Backend data structure: { summary, byStatus, activities, assignments }
    const data = (response as any)?.data || response;
    return data;
  },

  /** Cursor-paged older activity for the Activity tab ("Load older history"). */
  getActivityFeed: async (before?: string): Promise<{ items: any[]; nextBefore: string | null }> => {
    const qs = before ? `?before=${encodeURIComponent(before)}` : "";
    const response = await handleApiCall(async () => await api.get({
      url: getApiUrl(endpoint.leadAssignment.getActivityFeed) + qs,
      auth: true,
    }));
    const raw = (response as any) || {};
    const data = raw.items ? raw : raw.data?.items ? raw.data : raw.data?.data || {};
    return { items: data.items || [], nextBefore: data.nextBefore || null };
  },

  /** Milestones panel: appointments booked + policies (underwriting / sold), cursor paged. */
  getMilestones: async (type: string, before?: string): Promise<{ items: any[]; nextBefore: string | null }> => {
    const qs = new URLSearchParams({ type, ...(before ? { before } : {}) }).toString();
    const response = await handleApiCall(async () => await api.get({
      url: `${getApiUrl(endpoint.leadAssignment.getMilestones)}?${qs}`,
      auth: true,
    }));
    const raw = (response as any) || {};
    const data = raw.items ? raw : raw.data?.items ? raw.data : raw.data?.data || {};
    return { items: data.items || [], nextBefore: data.nextBefore || null };
  },

  /**
   * Per-agent pipeline-stage counts (system + custom stages) for Status Overview.
   * Returns { agents: { [agentId]: { [stageName]: count } } }
   */
  getAgentStatusBreakdown: async (): Promise<{
    agents: Record<string, Record<string, number>>;
    agentNames: Record<string, string>;
    customStages: { name: string; owners: string[]; assignedCount: number }[];
  }> => {
    const response = await handleApiCall(async () => await api.get({
      url: getApiUrl(endpoint.leadAssignment.getAgentStatusBreakdown),
      auth: true,
    }));
    // handleApiCall already unwraps { statusCode, data } -> data, so the
    // payload is usually the object itself; tolerate wrapped shapes anyway.
    const raw = (response as any) || {};
    const data = raw.agents || raw.customStages ? raw : raw.data?.agents ? raw.data : raw.data?.data || {};
    return { agents: data.agents || {}, agentNames: data.agentNames || {}, customStages: data.customStages || [] };
  },

  /**
   * Assign all leads from a specific CSV import to an agent
   * @param importId - The ID of the import to get leads from
   * @param agentId - The ID of the agent to assign leads to
   */
  assignFromImport: async (
    importId: string,
    agentId: string,
    cleanSlate?: boolean,
    /**
     * Take leads that currently sit with ANOTHER agent. Default false —
     * uploading a vendor list must not silently empty a teammate's book
     * (2026-08-21). Only pass true when the owner has seen who loses what
     * and chosen it.
     */
    moveFromOtherAgents?: boolean,
    /** Chunk start: the backend assigns 2,000 of the list per call from here. */
    offset?: number,
  ): Promise<{
    success: boolean;
    message: string;
    assignedCount: number;
    /** Leads in the whole list, and where the next chunk starts (>= total = done). */
    total: number;
    nextOffset: number;
    skippedCount: number;
    skippedLeads: string[] | null;
    movedCount: number;
    movedLeads: { name: string; movedFrom: string }[] | null;
    crossAgentSkippedCount: number;
    crossAgentSkipped: { id: string; name: string; heldBy: string }[] | null;
    canMoveFromOtherAgents: boolean;
    importId: string;
    agentId: string;
  }> => {
    const response = await handleApiCall(async () => await api.post({
      url: getApiUrl(endpoint.leadAssignment.assignFromImport),
      body: {
        importId,
        agentId,
        ...(cleanSlate && { cleanSlate: true }),
        ...(moveFromOtherAgents && { moveFromOtherAgents: true }),
        ...(offset !== undefined && { offset }),
      },
      auth: true,
      // Up to 2,000 leads per call; same headroom as an assign batch.
      options: { timeout: 90_000 },
    }));
    // Backend returns: { statusCode, message, data: { success, assignedCount, skippedCount, skippedLeads, importId, agentId, results } }
    // handleApiCall may return response.data or response.data.data depending on wrapper
    const rawData = (response as any)?.data?.data || (response as any)?.data || response;
    return {
      success: rawData?.success ?? false,
      message: rawData?.message || (response as any)?.message || 'Assignment completed',
      assignedCount: rawData?.assignedCount ?? 0,
      // An older backend assigns the whole list in one call and sends neither.
      total: rawData?.total ?? 0,
      nextOffset: rawData?.nextOffset ?? rawData?.total ?? 0,
      // skippedCount was being dropped here — modal couldn't tell the
      // agent "1 new lead assigned, 1866 were dups" because this number
      // never reached it. Re-add so toasts can surface the full picture.
      skippedCount: rawData?.skippedCount ?? 0,
      skippedLeads: Array.isArray(rawData?.skippedLeads) ? rawData.skippedLeads : null,
      // Leads that were with a DIFFERENT agent and moved to the target —
      // the owner's pick wins; movedFrom says who each one came from.
      movedCount: rawData?.movedCount ?? 0,
      movedLeads: Array.isArray(rawData?.movedLeads) ? rawData.movedLeads : null,
      // Left where they were, because another agent holds them. The modal
      // surfaces these and can re-POST with moveFromOtherAgents to take them.
      crossAgentSkippedCount: rawData?.crossAgentSkippedCount ?? 0,
      crossAgentSkipped: Array.isArray(rawData?.crossAgentSkipped) ? rawData.crossAgentSkipped : null,
      canMoveFromOtherAgents: !!rawData?.canMoveFromOtherAgents,
      importId: rawData?.importId || importId,
      agentId: rawData?.agentId || agentId,
    };
  },
};
